// Copyright (c) StrangeBee 2023

import { ReactElement, memo, useCallback } from 'react';

import { TextPreview } from './TextPreview';
import { PDFPreview } from './PDFPreview';
import { ImagePreview } from './ImagePreview';
import { CSVPreview } from './CSVPreview';
import { HTMLPreview } from './HTMLPreview';
import { MarkdownPreview } from './MarkdownPreview';

import './THPreview.scss';

export type THPreviewType = 'text' | 'image' | 'pdf' | 'csv' | 'markdown' | 'html';

interface ContainerProps {
    children: ReactElement;
    onClick: () => void;
}

const Container = ({ children, onClick }: ContainerProps): ReactElement => {
    const handleClick = useCallback(
        (event) => {
            event.stopPropagation();
            onClick();
        },
        [onClick],
    );

    return (
        <div className="THPreview" onClick={handleClick}>
            {children}
        </div>
    );
};

interface THPreviewProps {
    src: string;
    onClick: any;
    type?: THPreviewType | null;
    extension?: string | null;
}

const THPreview = ({ src, onClick, type, extension }: THPreviewProps): ReactElement | null => {
    switch (type) {
        case 'image':
            return (
                <Container onClick={onClick}>
                    <ImagePreview src={src} className="THPreview__Image" />
                </Container>
            );
        case 'csv':
            return (
                <Container onClick={onClick}>
                    <CSVPreview src={src} className="THPreview__CSV" />
                </Container>
            );

        case 'text':
            if (!!extension && extension === 'html') {
                return (
                    <Container onClick={onClick}>
                        <HTMLPreview src={src} className="THPreview__Html" />
                    </Container>
                );
            }

            return (
                <Container onClick={onClick}>
                    <TextPreview src={src} className="THPreview__Text" />
                </Container>
            );

        case 'pdf':
            return (
                <Container onClick={onClick}>
                    <PDFPreview src={src} className="THPreview__PDF" />
                </Container>
            );

        case 'markdown':
            return (
                <Container onClick={onClick}>
                    <MarkdownPreview src={src} className="THPreview__Markdown" />
                </Container>
            );

        default:
            return null;
    }
};

export default memo(THPreview);
